import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FlagGroup, UnderwritingStatus } from '../../../../shared/interfaces';
import { FlagManagerIssueDetailComponent } from '../flag-manager-issue-detail/flag-manager-issue-detail.component';
import { FlagManagerService } from '../../flag-manager.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-flag-manager-top-bar',
  templateUrl: './flag-manager-top-bar.component.html',
  styleUrls: ['./flag-manager-top-bar.component.scss'],
})
export class FlagManagerTopBarComponent implements OnInit, OnDestroy, OnChanges {
  @Output() tabChanged = new EventEmitter<string>();
  @Input() group: FlagGroup;
  @Input() countTabs;
  @Input() selectedTab: string;
  @Input() underwritingStatus: number = 0;
  @Input() allFlags: any[] = [];

  @Input() issueNo: number = 0;
  @Input() currentFlags: any[];
  @Output() editGroup = new EventEmitter<boolean>();

  @ViewChild('issueDetailModal', { static: false }) issueDetailModal!: FlagManagerIssueDetailComponent;

  hasChangesAfterLetter = false;
  private subscription = new Subscription();

  selectTab(tab: string) {
    this.selectedTab = tab;
    this.tabChanged.emit(tab);
  }

  isTabEnabled(tab: string): boolean {
    const hasGroup = !!this.group;
    const { included = 0, resolved = 0, excluded = 0, toResolve = 0 } = this.countTabs || {};

    switch (tab) {
      case 'included':
        return included > 0;

      case 'toResolve':
        if (!hasGroup) return false;
        return this.underwritingStatus >= UnderwritingStatus.LETTER_SENT && toResolve > 0;

      case 'resolved':
        if (!hasGroup) return false;
        return this.underwritingStatus >= UnderwritingStatus.RESOLUTION_IN_PROGRESS && resolved > 0;

      case 'excluded':
        return excluded > 0;

      default:
        return false;
    }
  }

  constructor(private flagManagerService: FlagManagerService) {}

  ngOnInit(): void {
    this.subscription.add(
      this.flagManagerService.getLetters().subscribe(() => {
        this.checkForChangesAfterLetter();
      }),
    );

    this.subscription.add(
      this.flagManagerService.getFlagsUpdated().subscribe(() => {
        // Delay to ensure flags are fully updated with changes and Angular change detection completes
        setTimeout(() => {
          this.checkForChangesAfterLetter();
        }, 1000);
      }),
    );

    this.checkForChangesAfterLetter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allFlags']) {
      // Small delay to ensure changes array is populated
      setTimeout(() => {
        this.checkForChangesAfterLetter();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  checkForChangesAfterLetter(): void {
    const latestLetter = this.flagManagerService.getLatestLetter();

    if (!latestLetter || !this.allFlags || this.allFlags.length === 0) {
      this.hasChangesAfterLetter = false;
      return;
    }

    // Get letter creation timestamp
    const letterCreatedAt = new Date(latestLetter.createdAt).getTime();

    const allChanges = this.allFlags
      .filter(flag => flag.changes && flag.changes.length > 0)
      .flatMap(flag => flag.changes);

    if (allChanges.length === 0) {
      this.hasChangesAfterLetter = false;
      return;
    }

    const hasChangesAfter = allChanges.some(change => {
      const changeTimestamp = new Date(change.date).getTime();
      return changeTimestamp > letterCreatedAt;
    });

    this.hasChangesAfterLetter = hasChangesAfter;
  }

  get lastEditedDate(): string {
    if (!this.allFlags || this.allFlags.length === 0) {
      return 'N/A';
    }

    const allChanges = this.allFlags
      .filter(flag => flag.changes && flag.changes.length > 0)
      .flatMap(flag => flag.changes);

    if (allChanges.length === 0) {
      return 'N/A';
    }

    const latestChange = allChanges.reduce((latest, change) => {
      const changeDate = new Date(change.date);
      const latestDate = latest ? new Date(latest.date) : new Date(0);
      return changeDate > latestDate ? change : latest;
    }, null);

    if (!latestChange) {
      return 'N/A';
    }

    const date = new Date(latestChange.date);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  }

  openIssueDetailModal() {
    this.issueDetailModal.openModal();
  }
}
